import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import LabFilters, { emptyLabFilter, labFilterIsOn,
                     matchesLabFilter } from '../../components/common/LabFilters';
import { apiRequest, eventDayLabel, eventStartLabel } from '../../utils';
import './MemberHome.css';

/* An event the company bought from us (``manage`` on the row) opens the game Home on that event —
   where its teams are — and not the public event page, which a private project does not have. */
const openManaged = (eventId) => {
  apiRequest('POST', '/manage-event', { event_id: eventId })
    .then((res) => { window.location.href = (res && res.redirect) || '/event-home'; })
    .catch(() => {});
};


const fallbackEvent = require('./../../assets/images/fallback/event.png');

/* All past Bravos Labs — reached from the "Show more" under the member/contact home calendar
   (which shows only the 5 most recent). Read-only rows that open the public event page. Reuses
   the same GET /api/bravos/home payload (its calendar carries an is_past flag), so no new
   endpoint.

   ★ Sifted exactly as the upcoming twin is — same controls, same match rule
   (`components/common/LabFilters`). Only the attendance chips read differently: on a list of
   things that already happened, "I'm in" is a memory rather than a plan. */

const PastLabs = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [past, setPast] = useState(null);
  const [catalog, setCatalog] = useState({ tags: [], purpose: null });
  const [filter, setFilter] = useState(emptyLabFilter());

  useEffect(() => {
    let cancelled = false;
    apiRequest('GET', '/bravos/home')
      .then((data) => {
        if (cancelled) return;
        const rows = (data.calendar || []).filter((e) => e.is_past);
        rows.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
        setPast(rows);
        setCatalog({ tags: data.tag_catalog || [], purpose: data.purpose_catalog });
      })
      .catch(() => { if (!cancelled) setPast([]); });
    return () => { cancelled = true; };
  }, []);

  const shown = (past || []).filter((e) => matchesLabFilter(e, filter));

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/member-home" />
        <Title my="1" className="mt-3">
          <Icon name="calendar" theme="primary" className="me-2" />
          {t('pastLabsTitle')}
        </Title>

        {past !== null && past.length > 1 && (
          <LabFilters
            events={past}
            tagCatalog={catalog.tags}
            purposeCatalog={catalog.purpose}
            value={filter}
            onChange={setFilter}
            past
          />
        )}

        {past === null ? (
          <span className="auth__spinner" />
        ) : past.length === 0 ? (
          <p className="text-muted mb-0">{t('noUpcomingEvents')}</p>
        ) : shown.length === 0 && labFilterIsOn(filter) ? (
          <p className="text-muted mb-0">{t('noEventsForFilter')}</p>
        ) : (
          shown.map((e) => (
            <div
              key={e.id}
              className="bravos-event"
              role="button"
              tabIndex={0}
              onClick={() => (e.manage ? openManaged(e.id) : navigate(`/public-event/${e.id}`))}
              onKeyDown={(ev) => { if (ev.key === 'Enter') { if (e.manage) openManaged(e.id); else navigate(`/public-event/${e.id}`); } }}
            >
              <span
                className="bravos-event__thumb"
                style={{ backgroundImage: `url("${e.image_link || fallbackEvent}")` }}
              />
              <span className="bravos-event__body">
                <span className="bravos-event__name">{e.name}</span>
                <span className="bravos-event__meta">
                  {[eventDayLabel(e), eventStartLabel(e), e.location_name]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
                {(e.host_names || e.subject) && (
                  <span className="bravos-event__sub">
                    {[e.host_names ? t('hostedByName', { name: e.host_names }) : null,
                      e.subject].filter(Boolean).join(' · ')}
                  </span>
                )}
              </span>
              <Icon name="angle-right" className="bravos-event__chev" />
            </div>
          ))
        )}
      </Card>
    </Base>
  );
};

export default PastLabs;
